import React from "react";
import {T, useTranslate} from "@tolgee/react";
import {BasicInput, Checkbox, InputGroup} from "view/components/Form";
import {Box, makeStyles, Typography} from "@material-ui/core";
import {UseFormReturn} from "react-hook-form";
import useStandAreaCalculation from "tools/stand/calculateStandArea";
import {grey} from "@material-ui/core/colors";
import {unitSystems} from "constraints/general";
import {StandDetailsFormState} from "view/SignUp/SignUpToken/context/types";
import {useFormStateContext} from "view/SignUp/SignUpToken/context";
import useLocalizedLocalMap from "hooks/useLocalizedLocalMap";
import {ShowUnitSystem} from "@evennt-tech/api-v2-core";

type Props = {
  form: UseFormReturn<StandDetailsFormState>;
  unitSystem: ShowUnitSystem;
};

const StandSizes = ({form, unitSystem}: Props) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const {watch} = form;
  const {show} = useFormStateContext();
  const localMap = useLocalizedLocalMap(show);
  if (!show) {
    return null;
  }

  const isIrregularSelected = watch("isIrregularSelected");
  useStandAreaCalculation(form);
  const unitObj =
    unitSystems.find((system) => system.value === unitSystem) ?? unitSystems[0];
  const {units: unit} = unitObj;
  const localizedStand = localMap.stand;
  const localizedWallLength = localMap.standWallLength;

  return (
    <Box className={classes.main}>
      <Typography variant="h3" className={classes.header}>
        <T
          keyName="global.sign_up.stand_size"
          params={{stand: localizedStand}}
        />
      </Typography>
      <InputGroup>
        <BasicInput
          label={t("global.sign_up.stand_width", {
            stand: localizedStand,
            unit,
          })}
          name="standWidth"
          required
          rules={{
            required: true,
            valueAsNumber: true,
            min: 0,
          }}
          InputProps={{
            inputProps: {
              min: 0,
              step: "any",
            },
          }}
          type="number"
        />
        <BasicInput
          label={t("global.sign_up.stand_depth", {
            stand: localizedStand,
            unit,
          })}
          name="standLength"
          required
          rules={{
            required: true,
            valueAsNumber: true,
            min: 0,
          }}
          InputProps={{
            inputProps: {
              min: 0,
              step: "any",
            },
          }}
          type="number"
        />
      </InputGroup>
      <InputGroup>
        <BasicInput
          label={t("global.sign_up.stand_area", {unit})}
          required
          name="irregularArea"
          rules={{
            required: true,
            valueAsNumber: true,
            min: 0,
          }}
          disabled={!isIrregularSelected}
          InputProps={{
            inputProps: {
              min: 0,
              step: "any",
            },
          }}
          InputLabelProps={{shrink: true}}
          type="number"
        />
        <BasicInput
          label={t("global.sign_up.wall_length_optional", {
            wallLength: localizedWallLength,
          })}
          name="wallLength"
          required={false}
          rules={{
            required: false,
            valueAsNumber: true,
            min: 0,
          }}
          InputProps={{
            inputProps: {
              min: 0,
              step: "any",
            },
          }}
          type="number"
        />
      </InputGroup>
      <Checkbox
        color="primary"
        size="small"
        label={t("global.sign_up.irregular")}
        name="isIrregularSelected"
        defaultChecked={Boolean(isIrregularSelected)}
      />
    </Box>
  );
};

const useStyles = makeStyles(({spacing}) => ({
  main: {
    padding: spacing(3),
    borderRadius: 8,
    backgroundColor: grey["50"],
    border: `1px solid ${grey["300"]}`,
  },
  header: {
    marginBottom: spacing(2),
  },
}));
export default StandSizes;
